import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import { Button } from "@/components/ui/button";
import CanonicalTag from "@/components/CanonicalTag";

const TransitionalADUDesignMilpitas = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Transitional ADU Design & Construction in Milpitas",
    "description": "Professional Transitional ADU design and construction services in Milpitas, CA. Custom accessory dwelling units blending traditional and contemporary elements.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://udaadu.com"
    },
    "areaServed": "Milpitas, CA",
    "serviceType": "Transitional ADU Design & Construction"
  };

  return (
    <>
      <CanonicalTag url="https://udaadu.com/transitional-adu-design-milpitas" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Transitional ADU Design & Construction in Milpitas
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Perfect balance of traditional charm and contemporary sophistication in your Milpitas Transitional ADU design.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={openCalendlyModal}
                size="lg"
                className="bg-white text-purple-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Get Free Consultation
              </Button>
              <Link to="/cost-estimate">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-purple-900 px-8 py-4 text-lg font-semibold"
                >
                  Get Cost Estimate
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Transitional Style */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Why Choose Transitional ADU Design in Milpitas?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Perfect Balance</h3>
                <p className="text-gray-200">
                  Transitional design bridges traditional comfort with contemporary functionality, creating timeless yet current spaces.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Versatile Appeal</h3>
                <p className="text-gray-200">
                  Appeals to a wide range of tastes and preferences, making your ADU attractive to diverse tenants or family members.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Flexible Design</h3>
                <p className="text-gray-200">
                  Easily adaptable to changing needs and trends while maintaining classic sophistication and comfort.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Transitional ADU Design Features
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Mixed Materials</h3>
                <p className="text-gray-200">
                  Thoughtful combination of wood, stone, metal, and glass creates sophisticated, layered aesthetics.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Neutral Palettes</h3>
                <p className="text-gray-200">
                  Warm grays, soft beiges, and crisp whites provide elegant backdrops for both classic and modern furnishings.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Clean Lines</h3>
                <p className="text-gray-200">
                  Simplified moldings and trim work create streamlined elegance without sacrificing architectural interest.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Modern Amenities</h3>
                <p className="text-gray-200">
                  Contemporary kitchens and baths with traditional styling elements provide the best of both worlds.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Flexible Layouts</h3>
                <p className="text-gray-200">
                  Open floor plans with defined spaces create flow while maintaining comfortable, intimate areas.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Quality Finishes</h3>
                <p className="text-gray-200">
                  Premium materials and finishes in understated designs create lasting beauty and value.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Milpitas Local Considerations */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Milpitas Transitional ADU Considerations
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-white mb-4">Local Regulations</h3>
                <ul className="space-y-3 text-gray-200">
                  <li>• Maximum 1,200 sq ft for detached ADUs</li>
                  <li>• 5-foot minimum setbacks required</li>
                  <li>• Height limit of 16 feet maximum</li>
                  <li>• Owner-occupancy requirements apply</li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-white mb-4">Design Integration</h3>
                <ul className="space-y-3 text-gray-200">
                  <li>• Complement existing neighborhood architecture</li>
                  <li>• Balance traditional and contemporary elements</li>
                  <li>• Meet California energy efficiency standards</li>
                  <li>• Consider seismic safety requirements</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Transitional ADU Investment in Milpitas
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Transitional ADUs range from $200,000 to $410,000 depending on size and finishes. 
              The versatile style ensures broad appeal and strong rental potential in the Milpitas market.
            </p>
            <Link to="/cost-estimate">
              <Button
                size="lg"
                className="bg-white text-purple-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Get Detailed Cost Estimate
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Ready to Build Your Transitional ADU in Milpitas?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Let's create a beautiful Transitional ADU that perfectly balances traditional and contemporary design.
            </p>
            <Button
              onClick={openCalendlyModal}
              size="lg"
              className="bg-white text-purple-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
            >
              Schedule Your Free Consultation
            </Button>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              Explore More ADU Designs in Milpitas
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/contemporary-adu-design-milpitas" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Contemporary ADU Design Milpitas</h3>
                <p className="text-gray-200">Modern, sleek designs for today's lifestyle</p>
              </Link>
              <Link to="/craftsman-adu-design-milpitas" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Craftsman ADU Design Milpitas</h3>
                <p className="text-gray-200">Traditional craftsmanship with modern amenities</p>
              </Link>
              <Link to="/modern-farmhouse-adu-design-milpitas" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Modern Farmhouse ADU Design Milpitas</h3>
                <p className="text-gray-200">Rustic charm meets contemporary comfort</p>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </>
  );
};

export default TransitionalADUDesignMilpitas;